/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import i18n from '../../i18n';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIRfidTokensResponseInterface,
  CreateTokenPayload,
  DeactivateTokenPayload,
  RequestTokensInterface,
  RfidSummaryResponse,
  RfidToken,
  RfidTokensInitialProperties,
  TokenStatus,
  UpdateTokenPayload,
} from '../../interface/rfid-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import rfidApi from '../../utils/api/rfid';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Normalizes raw token data from API responses
 * @param {any} item - Raw token data object
 * @returns {RfidToken} Normalized RFID token entity
 */
const formatTokenItem = (item: any): RfidToken => {
  let poolBalance: number | undefined;
  const rawPoolBalance = item.cardPool?.balanceKwh ?? item.balanceKwh;
  if (
    typeof rawPoolBalance === 'object' &&
    rawPoolBalance !== null &&
    '$numberDecimal' in rawPoolBalance
  ) {
    poolBalance = Number.parseFloat(rawPoolBalance.$numberDecimal) || 0;
  } else if (typeof rawPoolBalance === 'number') {
    poolBalance = rawPoolBalance;
  } else if (typeof rawPoolBalance === 'string') {
    poolBalance = Number.parseFloat(rawPoolBalance) || 0;
  }

  const cardPoolId =
    item.cardPoolId === null
      ? null
      : item.cardPoolId || item.cardPool?._id || item.cardPool?.id || null;
  const cardPoolName =
    item.cardPoolId === null ? null : item.cardPool?.name || item.cardPoolName || null;
  const stationIds =
    item.cardPool?.stationIds || item.stationIds || (item.stationId ? [item.stationId] : []);

  return {
    ...item,
    id: item.id || item._id || '',
    _id: item._id || item.id || '',
    cardPoolId,
    cardPoolName,
    balanceKwh: poolBalance !== undefined ? poolBalance : item.balanceKwh,
    stationIds,
    createdAt: item.createdAt ? ConvertUTCtoDate(item.createdAt) : '',
    deactivatedAt: item.deactivatedAt ? ConvertUTCtoDate(item.deactivatedAt) : null,
  };
};

/**
 * Async thunk to fetch all RFID tokens with query parameters
 */
export const fetchAllTokens = createAsyncThunk<
  APIRfidTokensResponseInterface,
  RequestTokensInterface
>('rfid-tokens/fetchAllTokens', async (parameters: RequestTokensInterface, { dispatch }) => {
  try {
    const response = await rfidApi.fetchTokens(removeEmptyKeys(parameters));
    const rawResults =
      response?.data?.results ||
      response?.data?.data ||
      (Array.isArray(response?.data) ? response?.data : []);

    const formattedList: RfidToken[] = Array.isArray(rawResults)
      ? rawResults.map((item: any) => formatTokenItem(item))
      : [];

    return {
      results: formattedList,
      limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
      page: response?.data?.page || parameters.page || DefaultApiParameters.page,
      totalPages: response?.data?.totalPages || 0,
      totalResults: response?.data?.totalResults || formattedList.length,
    };
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message || i18n.t('devices:failedToFetchRfidCards'),
        code: error?.response?.data?.code || 500,
        status: true,
      }),
    );
    throw error;
  }
});

/**
 * Async thunk to refresh RFID tokens list
 */
export const refreshTokens = createAsyncThunk<
  APIRfidTokensResponseInterface,
  RequestTokensInterface
>('rfid-tokens/refreshTokens', async (parameters: RequestTokensInterface) => {
  const response = await rfidApi.fetchTokens(removeEmptyKeys(parameters));
  const rawResults =
    response?.data?.results ||
    response?.data?.data ||
    (Array.isArray(response?.data) ? response?.data : []);

  const formattedList: RfidToken[] = Array.isArray(rawResults)
    ? rawResults.map((item: any) => formatTokenItem(item))
    : [];

  return {
    results: formattedList,
    limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
    page: response?.data?.page || parameters.page || DefaultApiParameters.page,
    totalPages: response?.data?.totalPages || 0,
    totalResults: response?.data?.totalResults || formattedList.length,
  };
});

/**
 * Async thunk to fetch a single RFID token by ID
 */
export const fetchTokenById = createAsyncThunk<RfidToken, string, { rejectValue: string }>(
  'rfid-tokens/fetchTokenById',
  async (tokenId: string, { rejectWithValue }) => {
    try {
      const response = await rfidApi.fetchTokenById(tokenId);
      const data = response?.data?.results || response?.data?.data || response?.data || response;
      return formatTokenItem({ ...data, id: data?.id || data?._id || tokenId });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToFetchRfidCardDetails');
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to create a new RFID token
 */
export const createToken = createAsyncThunk<RfidToken, CreateTokenPayload, { rejectValue: string }>(
  'rfid-tokens/createToken',
  async (payload: CreateTokenPayload, { dispatch, rejectWithValue }) => {
    try {
      const apiPayload: any = {
        uid: payload.uid.trim().toUpperCase(),
        type: payload.type,
      };

      if (payload.type === 'BUSINESS_TAP') {
        if (payload.cardPoolId) {
          apiPayload.cardPoolId = payload.cardPoolId;
        }
        if (Array.isArray(payload.stationIds) && payload.stationIds.length > 0) {
          apiPayload.stationIds = payload.stationIds;
        } else if (payload.stationId) {
          apiPayload.stationIds = [payload.stationId];
        }
        if (payload.customerId) {
          apiPayload.customerId = payload.customerId;
        }
      } else if (payload.type === 'PERSONAL_TAP') {
        if (payload.customerId) {
          apiPayload.customerId = payload.customerId;
        }
      }

      const response = await rfidApi.createToken(apiPayload);
      const responseData = response?.data?.results || response?.data || response;
      const createdToken: RfidToken = formatTokenItem({
        ...responseData,
        id: responseData.id || responseData._id || `tok_${Date.now()}`,
        status: responseData.status || 'ACTIVE',
        issuedBy: responseData.issuedBy || 'TENANT',
      });

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'success',
          message: i18n.t('devices:rfidCardRegisteredSuccessfully'),
          code: 200,
        }),
      );

      return createdToken;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToCreateRfidCard');
      const statusCode = error?.response?.data?.code || error?.response?.status || 500;

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorMessage,
          code: statusCode,
        }),
      );
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to update an existing RFID token
 */
export const updateToken = createAsyncThunk<
  RfidToken,
  { tokenId: string; data: UpdateTokenPayload },
  { rejectValue: string }
>('rfid-tokens/updateToken', async ({ tokenId, data }, { dispatch, rejectWithValue }) => {
  try {
    const response = await rfidApi.updateToken(tokenId, data);
    const responseData =
      response?.data?.results || response?.data?.data || response?.data || response;
    const updatedToken: RfidToken = formatTokenItem({
      ...responseData,
      id: responseData.id || responseData._id || tokenId,
      stationIds: data.stationIds !== undefined ? data.stationIds : responseData.stationIds,
      customerId: data.customerId !== undefined ? data.customerId : responseData.customerId,
      linkedCustomerId:
        data.linkedCustomerId !== undefined ? data.linkedCustomerId : responseData.linkedCustomerId,
      cardPoolId: data.cardPoolId !== undefined ? data.cardPoolId : responseData.cardPoolId,
    });

    dispatch(
      setNotificationMessage({
        status: true,
        type: 'success',
        message: i18n.t('devices:rfidCardUpdatedSuccessfully'),
        code: 200,
      }),
    );

    return updatedToken;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      i18n.t('devices:failedToUpdateRfidCard');
    const statusCode = error?.response?.data?.code || error?.response?.status || 500;

    dispatch(
      setNotificationMessage({
        status: true,
        type: 'error',
        message: errorMessage,
        code: statusCode,
      }),
    );
    return rejectWithValue(errorMessage);
  }
});

/**
 * Async thunk to deactivate a token permanently
 */
export const deactivateToken = createAsyncThunk<
  { tokenId: string; status: TokenStatus; reason: string; deactivatedAt: string },
  DeactivateTokenPayload,
  { rejectValue: string }
>(
  'rfid-tokens/deactivateToken',
  async (payload: DeactivateTokenPayload, { dispatch, rejectWithValue }) => {
    try {
      const timestamp = new Date().toISOString();
      const response = await rfidApi.changeActivateToken(payload.tokenId, {
        reason: payload?.reason || undefined,
        status: payload.status === 'ACTIVE' ? 'deActive' : 'active',
      });

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'success',
          message: i18n.t('devices:rfidCardDeactivatedPermanently'),
          code: 200,
        }),
      );

      const responseData = response?.data?.results || response?.data?.data || response?.data;
      const tokenStatus: TokenStatus = payload.status === 'ACTIVE' ? 'ACTIVE' : 'DEACTIVATED';

      return {
        tokenId: payload.tokenId,
        status: tokenStatus,
        reason: payload?.reason || '',
        deactivatedAt: responseData?.deactivatedAt || timestamp,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToDeactivateCard');
      const statusCode = error?.response?.data?.code || error?.response?.status || 500;

      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorMessage,
          code: statusCode,
        }),
      );
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to fetch token filter options
 */
export const fetchTokenFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'rfid-tokens/fetchTokenFilters',
  async (_, { dispatch }) => {
    try {
      const response = await rfidApi.fetchTokensFilterList();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || i18n.t('sharedComponent:somethingWentWrong'),
          },
        },
      });
      throw error;
    }
  },
);

/**
 * Async thunk to fetch RFID summary statistics
 */
export const fetchRfidSummary = createAsyncThunk<
  RfidSummaryResponse,
  void,
  { rejectValue: string }
>('rfid-tokens/fetchRfidSummary', async (_, { rejectWithValue }) => {
  try {
    const response = await rfidApi.fetchRfidSummary();
    const data = response?.data?.results || response?.data?.data || response?.data || response;
    return {
      totalCards: {
        total: data?.totalCards?.total ?? 0,
        active: data?.totalCards?.active ?? 0,
        deactivated: data?.totalCards?.deactivated ?? 0,
      },
      personalCards: data?.personalCards ?? 0,
      businessCards: data?.businessCards ?? 0,
      cardGroups: {
        total: data?.cardGroups?.total ?? 0,
        group: data?.cardGroups?.group ?? 0,
        individual: data?.cardGroups?.individual ?? 0,
      },
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      i18n.t('devices:failedToFetchRfidSummary');
    return rejectWithValue(errorMessage);
  }
});

/**
 * Compatibility exports matching legacy naming
 */
export const fetchTokensApi = fetchAllTokens;
export const fetchTokenByIdApi = fetchTokenById;
export const createTokenApi = createToken;
export const updateTokenApi = updateToken;
export const deactivateTokenApi = deactivateToken;
export const fetchTokenFiltersApi = fetchTokenFilters;
export const fetchRfidSummaryApi = fetchRfidSummary;

const availableFilters: CommonFilterProperties[] = [
  {
    name: i18n.t('devices:searchHere'),
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for RFID Tokens slice
 */
const initialState: RfidTokensInitialProperties = {
  tokens: [],
  selectedToken: null,
  summary: null,
  summaryLoading: false,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  actionLoading: false,
  tokenDetailsLoading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for RFID Tokens management
 */
export const rfidTokenSlice = createSlice({
  name: 'rfid-tokens',
  initialState,
  reducers: {
    /**
     * Sets selected token for side panel or modal
     * @param {RfidTokensInitialProperties} state - Current slice state
     * @param {PayloadAction<RfidToken | null>} action - Token payload
     */
    setSelectedToken: (state, action: PayloadAction<RfidToken | null>) => {
      state.selectedToken = action.payload;
    },

    /**
     * Updates token list filters
     * @param {RfidTokensInitialProperties} state - Current slice state
     * @param {PayloadAction<FilterUpdateProperties>} action - Filter update payload
     */
    updateTokenFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Disables the filter updated status flag
     * @param {RfidTokensInitialProperties} state - Current slice state
     */
    disableTokenFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Sets token sorting parameters
     * @param {RfidTokensInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setTokenSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Sets sorting parameters (alias matching other modules)
     * @param {RfidTokensInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Resets token state to initial values
     * @param {RfidTokensInitialProperties} state - Current slice state
     */
    resetTokenState: (state) => {
      state.tokens = [];
      state.selectedToken = null;
      state.summary = null;
      state.summaryLoading = false;
      state.loading = false;
      state.actionLoading = false;
      state.tokenDetailsLoading = false;
      state.filters = availableFilters;
      state.isFilterUpdated = false;
      state.page = DefaultApiParameters.page;
      state.limit = DefaultApiParameters.limit;
      state.totalPages = 0;
      state.totalResults = 0;
      state.sortBy = null;
      state.sortField = null;
    },
  },
  /**
   * Extra reducers builder for async thunk actions
   * @param {ActionReducerMapBuilder<RfidTokensInitialProperties>} builder - Redux Toolkit action builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RfidTokensInitialProperties>) => {
    // Fetch all tokens
    builder
      .addCase(fetchAllTokens.pending, (state: RfidTokensInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTokens.fulfilled,
        (
          state: RfidTokensInitialProperties,
          action: PayloadAction<APIRfidTokensResponseInterface>,
        ) => {
          state.loading = false;
          state.tokens = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllTokens.rejected, (state: RfidTokensInitialProperties) => {
        state.loading = false;
      });

    // Refresh tokens
    builder.addCase(
      refreshTokens.fulfilled,
      (
        state: RfidTokensInitialProperties,
        action: PayloadAction<APIRfidTokensResponseInterface>,
      ) => {
        state.tokens = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );

    // Single token details fetch by ID
    builder
      .addCase(fetchTokenById.pending, (state: RfidTokensInitialProperties) => {
        state.tokenDetailsLoading = true;
      })
      .addCase(
        fetchTokenById.fulfilled,
        (state: RfidTokensInitialProperties, action: PayloadAction<RfidToken>) => {
          state.tokenDetailsLoading = false;
          state.selectedToken = action.payload;
          const targetId = action.payload.id || action.payload._id;
          const index = state.tokens.findIndex(
            (t) => (t.id || t._id) === targetId || t.uid === action.payload.uid,
          );
          if (index !== -1) {
            state.tokens[index] = {
              ...state.tokens[index],
              ...action.payload,
            };
          }
        },
      )
      .addCase(fetchTokenById.rejected, (state: RfidTokensInitialProperties) => {
        state.tokenDetailsLoading = false;
      });

    // Create Token
    builder
      .addCase(createToken.pending, (state: RfidTokensInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        createToken.fulfilled,
        (state: RfidTokensInitialProperties, action: PayloadAction<RfidToken>) => {
          state.actionLoading = false;
          state.tokens = [action.payload, ...state.tokens];
          state.totalResults += 1;
        },
      )
      .addCase(createToken.rejected, (state: RfidTokensInitialProperties) => {
        state.actionLoading = false;
      });

    // Update Token
    builder
      .addCase(updateToken.pending, (state: RfidTokensInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        updateToken.fulfilled,
        (state: RfidTokensInitialProperties, action: PayloadAction<RfidToken>) => {
          state.actionLoading = false;
          const targetId = action.payload.id || action.payload._id;
          const index = state.tokens.findIndex((t) => (t.id || t._id) === targetId);
          if (index !== -1) {
            state.tokens[index] = {
              ...state.tokens[index],
              ...action.payload,
              cardPoolId:
                action.payload.cardPoolId !== undefined
                  ? action.payload.cardPoolId
                  : state.tokens[index].cardPoolId,
              cardPoolName:
                action.payload.cardPoolName !== undefined
                  ? action.payload.cardPoolName
                  : state.tokens[index].cardPoolName,
              stationIds: action.payload.stationIds || state.tokens[index].stationIds,
              customerId:
                action.payload.customerId !== undefined
                  ? action.payload.customerId
                  : state.tokens[index].customerId,
              linkedCustomerId:
                action.payload.linkedCustomerId !== undefined
                  ? action.payload.linkedCustomerId
                  : state.tokens[index].linkedCustomerId,
            };
          }
          if (
            state.selectedToken &&
            (state.selectedToken.id || state.selectedToken._id) === targetId
          ) {
            state.selectedToken = {
              ...state.selectedToken,
              ...action.payload,
              cardPoolId:
                action.payload.cardPoolId !== undefined
                  ? action.payload.cardPoolId
                  : state.selectedToken.cardPoolId,
              cardPoolName:
                action.payload.cardPoolName !== undefined
                  ? action.payload.cardPoolName
                  : state.selectedToken.cardPoolName,
              stationIds: action.payload.stationIds || state.selectedToken.stationIds,
              customerId:
                action.payload.customerId !== undefined
                  ? action.payload.customerId
                  : state.selectedToken.customerId,
              linkedCustomerId:
                action.payload.linkedCustomerId !== undefined
                  ? action.payload.linkedCustomerId
                  : state.selectedToken.linkedCustomerId,
            };
          }
        },
      )
      .addCase(updateToken.rejected, (state: RfidTokensInitialProperties) => {
        state.actionLoading = false;
      });

    // Deactivate Token
    builder
      .addCase(deactivateToken.pending, (state: RfidTokensInitialProperties) => {
        state.actionLoading = true;
      })
      .addCase(
        deactivateToken.fulfilled,
        (
          state: RfidTokensInitialProperties,
          action: PayloadAction<{
            tokenId: string;
            reason: string;
            status: string;
            deactivatedAt: string;
          }>,
        ) => {
          state.actionLoading = false;
          const targetId = action.payload.tokenId;
          const token = state.tokens.find(
            (t) => t.id === targetId || t._id === targetId || t.uid === targetId,
          );
          if (token) {
            token.status = action.payload.status as TokenStatus;
            token.reason = action.payload.reason;
            token.deactivatedAt = ConvertUTCtoDate(action.payload.deactivatedAt);
          }
          if (
            state.selectedToken &&
            (state.selectedToken.id === targetId ||
              state.selectedToken._id === targetId ||
              state.selectedToken.uid === targetId)
          ) {
            state.selectedToken.status = action.payload.status as TokenStatus;
            state.selectedToken.reason = action.payload.reason;
            state.selectedToken.deactivatedAt = ConvertUTCtoDate(action.payload.deactivatedAt);
          }
        },
      )
      .addCase(deactivateToken.rejected, (state: RfidTokensInitialProperties) => {
        state.actionLoading = false;
      });

    // Token filter options
    builder
      .addCase(fetchTokenFilters.pending, (state: RfidTokensInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchTokenFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchTokenFilters.rejected, (state: RfidTokensInitialProperties) => {
        state.filterListLoading = false;
      });

    // Fetch RFID summary statistics
    builder
      .addCase(fetchRfidSummary.pending, (state: RfidTokensInitialProperties) => {
        state.summaryLoading = true;
      })
      .addCase(
        fetchRfidSummary.fulfilled,
        (state: RfidTokensInitialProperties, action: PayloadAction<RfidSummaryResponse>) => {
          state.summaryLoading = false;
          state.summary = action.payload;
        },
      )
      .addCase(fetchRfidSummary.rejected, (state: RfidTokensInitialProperties) => {
        state.summaryLoading = false;
      });
  },
});

export const {
  setSelectedToken,
  updateTokenFilters,
  disableTokenFilterApplied,
  setTokenSortData,
  setSortData,
  resetTokenState,
} = rfidTokenSlice.actions;

export default rfidTokenSlice.reducer;
